import { Box, useTheme } from '@chronosphereio/chrono-ui';
import { CodeEditor, type CodeEditorProps } from '@chronosphereio/editors';
import { unifiedMergeView } from '@codemirror/merge';
import { type ReactNode, useMemo } from 'react';
import { useQueryEditorContext } from './query-editor-context';

export interface QueryEditorInputProps {
  extensions?: CodeEditorProps['extensions'];
  placeholder?: string;
  readOnly?: boolean;
  endAdornment?: ReactNode;
  /**
   * When provided, enables a unified merge/diff view showing changes between
   * this value and the current editor value. Source-agnostic — can be driven
   * by AI suggestions, version history, or any other comparison source.
   */
  previousValue?: string;
}

/**
 * CodeMirror editor wired to the nearest QueryEditor.Root context.
 *
 * Language-specific behavior (grammar, autocomplete, linting) is injected via
 * the `extensions` prop — the component itself is language-agnostic.
 */
export function QueryEditorInput({
  extensions,
  placeholder,
  readOnly,
  endAdornment,
  previousValue,
}: QueryEditorInputProps) {
  const { theme: t } = useTheme();
  const { value, onChange, onRun } = useQueryEditorContext();

  const mergedExtensions = useMemo(() => {
    const exts = extensions ? [...extensions] : [];
    if (previousValue !== undefined) {
      exts.push(
        unifiedMergeView({
          allowInlineDiffs: false,
          mergeControls: false,
          original: previousValue,
        })
      );
    }
    return exts;
  }, [extensions, previousValue]);

  const wrappedEndAdornment =
    endAdornment != null && endAdornment !== false ? (
      <Box
        display="flex"
        alignItems="flex-start"
        flexShrink={0}
        sx={{
          marginLeft: t.spacing(1),
          gap: t.spacing(0.25),
        }}
      >
        {endAdornment}
      </Box>
    ) : undefined;

  return (
    <CodeEditor
      value={value}
      onChange={onChange}
      onRun={onRun}
      extensions={mergedExtensions}
      placeholder={placeholder}
      readOnly={readOnly}
      endAdornment={wrappedEndAdornment}
      disableBorder
    />
  );
}
